import { Link } from "react-router-dom";
import { ArrowRight, Mountain, Layers, Handshake, Globe } from "lucide-react";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { IMAGES } from "../data/siteData";

const pillars = [
  {
    icon: Mountain,
    title: "Source Control",
    text: "Our own quarries and mines give us direct control over block selection, colour consistency and grade — batch after batch.",
  },
  {
    icon: Layers,
    title: "Complete Range",
    text: "Granite, marble and dolomite in blocks, slabs, tiles and processed forms — a single accountable source for every requirement.",
  },
  {
    icon: Globe,
    title: "India & International",
    text: "From our facility we dispatch across India and to export markets, with packing and documentation handled in-house.",
  },
  {
    icon: Handshake,
    title: "Relationships First",
    text: "Builders, contractors, architects, dealers and distributors return to us because we deliver what we commit to.",
  },
];

const About = () => (
  <main data-testid="about-page">
    <section className="bg-[#F8F9FA] py-20 md:py-28 border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>About Us</Eyebrow>
          <h1 className="mt-4 max-w-3xl text-5xl md:text-6xl font-bold tracking-tighter" data-testid="about-heading">
            Stone is not our trade. It is our ground.
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-gray-600" data-testid="about-intro">
            CelestraOneVentures Pvt. Ltd. is a granite, marble and dolomite company built around one principle:
            control the source, and quality follows. We operate our own quarries and mines with consistent quality,
            offer a wide range of stone under one roof, and bring years of experience and a trusted client base to
            every order — from a single slab to full container loads.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-24 bg-white">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <div className="relative">
            <div className="absolute -top-4 -left-4 h-full w-full border-2 border-[#D40000]" />
            <img
              src={IMAGES.quarry}
              alt="CelestraOneVentures quarry operations"
              className="relative h-[440px] w-full object-cover"
              data-testid="about-quarry-image"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="about-story-heading">
            From the quarry face to the finished edge
          </h2>
          <p className="mt-6 text-base leading-relaxed text-gray-600">
            Every order begins at the rock face. Our teams select and extract blocks at our own sites, move them to
            our processing facility, and cut, finish and grade them under one roof. That single chain of custody is
            why our clients — builders, architects, interior designers, stone dealers and exporters — can specify
            with confidence.
          </p>
          <p className="mt-4 text-base leading-relaxed text-gray-600">
            Whether you are building a residence, a commercial landmark or stocking a dealership, we size, finish
            and pack to your requirement and deliver to your site or port.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-24 bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>What We Stand On</Eyebrow>
          <h2 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight">Four pillars of every order</h2>
        </Reveal>
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div
                className="h-full border border-gray-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-black"
                data-testid={`pillar-card-${i}`}
              >
                <p.icon size={28} className="text-[#D40000]" strokeWidth={1.5} />
                <h3 className="mt-5 text-xl font-medium">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="noise-overlay bg-[#0A0A0A] py-20 text-white text-center">
      <div className="mx-auto max-w-3xl px-4">
        <Reveal>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Work with a supplier who owns the source</h2>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="mt-8 inline-flex items-center gap-2 bg-[#D40000] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-red-900"
          >
            Start an Enquiry <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default About;
